import {ClipboardEvent, KeyboardEvent, useEffect, useRef} from "react";
import {css, cva} from "@panda/css";

import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {useWebOtp} from "@pg-mono/user";

export const SMS_CODE_LENGTH = 4;

interface IProps {
    digits: string[];
    hasError: boolean;
    isBusy: boolean;
    autoFocus: boolean;
    focusRequestId: number;
    describedById?: string;
    onChange: (digits: string[]) => void;
    onComplete: (code: string) => void;
}

const fillDigits = (digits: string[], startIndex: number, value: string) => {
    const nextDigits = [...digits];
    const typedDigits = value
        .replace(/\D/g, "")
        .slice(0, SMS_CODE_LENGTH - startIndex)
        .split("");

    typedDigits.forEach((digit, offset) => {
        nextDigits[startIndex + offset] = digit;
    });

    return {nextDigits, lastFilledIndex: startIndex + typedDigits.length - 1};
};

export const RegistrationWizardSmsCodeInputs = (props: IProps) => {
    const {digits, hasError, isBusy, autoFocus, focusRequestId, describedById, onChange, onComplete} = props;
    const groupRef = useRef<HTMLDivElement>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const focusInput = (index: number) => {
        inputRefs.current[index]?.focus({preventScroll: true});
    };

    const canMoveFocus = () => autoFocus || Boolean(groupRef.current?.contains(document.activeElement));

    useEffect(() => {
        if (canMoveFocus()) {
            focusInput(0);
        }
    }, [focusRequestId]);

    const applyDigits = (nextDigits: string[], focusIndex: number, shouldMoveFocus = true) => {
        onChange(nextDigits);

        if (shouldMoveFocus) {
            focusInput(Math.min(focusIndex, SMS_CODE_LENGTH - 1));
        }

        if (nextDigits.every((digit) => digit !== "")) {
            onComplete(nextDigits.join(""));
        }
    };

    useWebOtp({
        enabled: !isBusy,
        onCodeReceived: (code) => {
            const {nextDigits} = fillDigits(["", "", "", ""], 0, code);
            applyDigits(nextDigits, SMS_CODE_LENGTH - 1, canMoveFocus());
        }
    });

    const handleChange = (index: number, value: string) => {
        if (isBusy) {
            return;
        }

        if (value === "") {
            const nextDigits = [...digits];
            nextDigits[index] = "";
            onChange(nextDigits);
            return;
        }

        const {nextDigits, lastFilledIndex} = fillDigits(digits, index, value);

        if (lastFilledIndex < index) {
            return;
        }

        applyDigits(nextDigits, lastFilledIndex + 1);
    };

    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace" && !isBusy && digits[index] === "" && index > 0) {
            event.preventDefault();
            const nextDigits = [...digits];
            nextDigits[index - 1] = "";
            onChange(nextDigits);
            focusInput(index - 1);
        }

        if (event.key === "ArrowLeft" && index > 0) {
            focusInput(index - 1);
        }

        if (event.key === "ArrowRight" && index < SMS_CODE_LENGTH - 1) {
            focusInput(index + 1);
        }
    };

    const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
        if (isBusy) {
            return;
        }

        const pasted = event.clipboardData.getData("text");

        if (!/\d/.test(pasted)) {
            return;
        }

        event.preventDefault();
        const {nextDigits, lastFilledIndex} = fillDigits(digits, index, pasted);
        applyDigits(nextDigits, lastFilledIndex + 1);
    };

    return (
        <div ref={groupRef} className={inputsRowStyle} role="group" aria-label="Kod z SMS" aria-describedby={describedById} aria-busy={isBusy}>
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    value={digit}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={index === 0 ? SMS_CODE_LENGTH : 1}
                    aria-label={`Cyfra ${index + 1} z ${SMS_CODE_LENGTH}`}
                    aria-invalid={hasError}
                    className={digitInputStyle({hasError, isBusy})}
                    onChange={(event) => handleChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={(event) => handlePaste(index, event)}
                    onFocus={(event) => event.target.select()}
                    data-testid={REGISTRATION_WIZARD_TEST_IDS.SMS_CODE_INPUT}
                />
            ))}
        </div>
    );
};

const inputsRowStyle = css({
    display: "flex",
    columnGap: 2
});

const digitInputStyle = cva({
    base: {
        flex: "1 1 0",
        minWidth: 0,
        height: "7.2rem",
        border: "1px solid var(--colors-secondary)",
        borderRadius: 1,
        backgroundColor: "var(--colors-white)",
        color: "var(--colors-secondary)",
        fontSize: "3.079rem",
        fontWeight: 600,
        lineHeight: "4rem",
        textAlign: "center",
        outline: "none",
        _focus: {
            boxShadow: "inset 0 0 0 1px var(--colors-secondary)"
        },
        md: {
            height: "9.5rem"
        }
    },
    variants: {
        hasError: {
            true: {
                borderColor: "var(--colors-danger)",
                _focus: {
                    boxShadow: "inset 0 0 0 1px var(--colors-danger)"
                }
            }
        },
        isBusy: {
            true: {
                opacity: 0.6
            }
        }
    }
});
import {ClipboardEvent, KeyboardEvent, useEffect, useRef} from "react";
import {css, cva} from "@panda/css";

import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {useWebOtp} from "@pg-mono/user";

export const SMS_CODE_LENGTH = 4;

interface IProps {
    digits: string[];
    hasError: boolean;
    isBusy: boolean;
    autoFocus: boolean;
    focusRequestId: number;
    describedById?: string;
    onChange: (digits: string[]) => void;
    onComplete: (code: string) => void;
}

const fillDigits = (digits: string[], startIndex: number, value: string) => {
    const nextDigits = [...digits];
    const typedDigits = value
        .replace(/\D/g, "")
        .slice(0, SMS_CODE_LENGTH - startIndex)
        .split("");

    typedDigits.forEach((digit, offset) => {
        nextDigits[startIndex + offset] = digit;
    });

    return {nextDigits, lastFilledIndex: startIndex + typedDigits.length - 1};
};

export const RegistrationWizardSmsCodeInputs = (props: IProps) => {
    const {digits, hasError, isBusy, autoFocus, focusRequestId, describedById, onChange, onComplete} = props;
    const groupRef = useRef<HTMLDivElement>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const focusInput = (index: number) => {
        inputRefs.current[index]?.focus({preventScroll: true});
    };

    const canMoveFocus = () => autoFocus || Boolean(groupRef.current?.contains(document.activeElement));

    useEffect(() => {
        if (canMoveFocus()) {
            focusInput(0);
        }
    }, [focusRequestId]);

    const applyDigits = (nextDigits: string[], focusIndex: number, shouldMoveFocus = true) => {
        onChange(nextDigits);

        if (shouldMoveFocus) {
            focusInput(Math.min(focusIndex, SMS_CODE_LENGTH - 1));
        }

        if (nextDigits.every((digit) => digit !== "")) {
            onComplete(nextDigits.join(""));
        }
    };

    useWebOtp({
        enabled: !isBusy,
        onCodeReceived: (code) => {
            const {nextDigits} = fillDigits(["", "", "", ""], 0, code);
            applyDigits(nextDigits, SMS_CODE_LENGTH - 1, canMoveFocus());
        }
    });

    const handleChange = (index: number, value: string) => {
        if (isBusy) {
            return;
        }

        if (value === "") {
            const nextDigits = [...digits];
            nextDigits[index] = "";
            onChange(nextDigits);
            return;
        }

        const {nextDigits, lastFilledIndex} = fillDigits(digits, index, value);

        if (lastFilledIndex < index) {
            return;
        }

        applyDigits(nextDigits, lastFilledIndex + 1);
    };

    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace" && !isBusy && digits[index] === "" && index > 0) {
            event.preventDefault();
            const nextDigits = [...digits];
            nextDigits[index - 1] = "";
            onChange(nextDigits);
            focusInput(index - 1);
        }

        if (event.key === "ArrowLeft" && index > 0) {
            focusInput(index - 1);
        }

        if (event.key === "ArrowRight" && index < SMS_CODE_LENGTH - 1) {
            focusInput(index + 1);
        }
    };

    const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
        if (isBusy) {
            return;
        }

        const pasted = event.clipboardData.getData("text");

        if (!/\d/.test(pasted)) {
            return;
        }

        event.preventDefault();
        const {nextDigits, lastFilledIndex} = fillDigits(digits, index, pasted);
        applyDigits(nextDigits, lastFilledIndex + 1);
    };

    return (
        <div ref={groupRef} className={inputsRowStyle} role="group" aria-label="Kod z SMS" aria-describedby={describedById} aria-busy={isBusy}>
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    value={digit}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={index === 0 ? SMS_CODE_LENGTH : 1}
                    aria-label={`Cyfra ${index + 1} z ${SMS_CODE_LENGTH}`}
                    aria-invalid={hasError}
                    className={digitInputStyle({hasError, isBusy})}
                    onChange={(event) => handleChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={(event) => handlePaste(index, event)}
                    onFocus={(event) => event.target.select()}
                    data-testid={REGISTRATION_WIZARD_TEST_IDS.SMS_CODE_INPUT}
                />
            ))}
        </div>
    );
};

const inputsRowStyle = css({
    display: "flex",
    columnGap: 2
});

const digitInputStyle = cva({
    base: {
        flex: "1 1 0",
        minWidth: 0,
        height: "7.2rem",
        border: "1px solid var(--colors-secondary)",
        borderRadius: 1,
        backgroundColor: "var(--colors-white)",
        color: "var(--colors-secondary)",
        fontSize: "3.079rem",
        fontWeight: 600,
        lineHeight: "4rem",
        textAlign: "center",
        outline: "none",
        _focus: {
            boxShadow: "inset 0 0 0 1px var(--colors-secondary)"
        },
        md: {
            height: "9.5rem"
        }
    },
    variants: {
        hasError: {
            true: {
                borderColor: "var(--colors-danger)",
                _focus: {
                    boxShadow: "inset 0 0 0 1px var(--colors-danger)"
                }
            }
        },
        isBusy: {
            true: {
                opacity: 0.6
            }
        }
    }
});
